/*
 * story-attribution.js — the multi-variant half of the blog attribution stamp.
 *
 * THE PROBLEM THIS EXISTS FOR. Harney teas are multi-variant (tin, sachets,
 * bulk), so most cards in the "Teas in this story" module render a
 * <quick-buy-drawer> / <quick-buy-popover> instead of a direct add form. Those
 * elements lazy-fetch `{{ product.url }}?view=quick-buy-*`, a fresh render of
 * the product form with NO article context — Liquid cannot read arbitrary query
 * params, so the stamp cannot be threaded server-side. Without this file, most
 * module revenue lands on orders with no attribution at all.
 *
 * HOW. One capture-phase `submit` listener on the document. Capture runs from
 * the document down to the target, so it fires before ProductForm's own
 * listener on the form, which is where `new FormData(this)` is built — the
 * hidden input is in the DOM by the time the request is serialized.
 *
 * SCOPE. It stamps only forms that resolve to an element carrying
 * `data-story-source`, which only sections/story-products.liquid emits. An
 * add-to-cart anywhere else on the site is untouched.
 *
 * VALUE. `{blogHandle}/{articleHandle}` exactly, read straight off the module
 * wrapper. The dashboard collector refuses any other shape and counts it, so
 * this file never constructs, normalises, or repairs the value.
 *
 * NOT HERE. No utm_ is ever added to a link: an internal utm_ starts a new GA4
 * campaign and session and corrupts this module's own attribution. No dataLayer
 * push either — a `story_add` event is inert until someone wires a GTM→GA4 tag,
 * and shipping an event nothing consumes is a false positive waiting to be
 * cited as evidence.
 */
(function () {
  'use strict';

  var SCOPE_ATTR = 'data-story-source';
  var PROPERTY_NAME = 'properties[_story_source]';
  var SURFACE_SELECTOR = 'quick-buy-drawer, quick-buy-popover';

  if (window.storyAttributionBound) {
    return;
  }
  window.storyAttributionBound = true;

  // Which module card opened which quick-buy surface. Only consulted if the
  // surface has been relocated out of the module subtree (the theme moves
  // elements carrying `append-body`; the quick-buy elements do not carry it
  // today, so this is a guard against a future theme change, not a live path).
  var lastTrigger = null;

  function sourceFromScope(element) {
    if (!element || typeof element.closest !== 'function') {
      return null;
    }

    var scope = element.closest('[' + SCOPE_ATTR + ']');

    if (!scope) {
      return null;
    }

    return scope.getAttribute(SCOPE_ATTR) || null;
  }

  function onClick(event) {
    var target = event.target;

    if (!target || typeof target.closest !== 'function') {
      return;
    }

    var trigger = target.closest('[aria-controls]');

    if (!trigger) {
      return;
    }

    var source = sourceFromScope(trigger);

    if (!source) {
      return;
    }

    lastTrigger = { id: trigger.getAttribute('aria-controls'), source: source };
  }

  function isAddToCartForm(form) {
    if (!form || form.tagName !== 'FORM') {
      return false;
    }

    if (form.getAttribute('is') === 'product-form') {
      return true;
    }

    return (form.getAttribute('action') || '').indexOf('/cart/add') > -1;
  }

  function resolveSource(form) {
    var scoped = sourceFromScope(form);

    if (scoped) {
      return scoped;
    }

    if (!lastTrigger || typeof form.closest !== 'function') {
      return null;
    }

    var surface = form.closest(SURFACE_SELECTOR);

    if (!surface || !surface.id || surface.id !== lastTrigger.id) {
      return null;
    }

    return lastTrigger.source;
  }

  function onSubmit(event) {
    var form = event.target;

    if (!isAddToCartForm(form)) {
      return;
    }

    var source = resolveSource(form);

    if (!source) {
      return;
    }

    var input = form.querySelector('input[name="' + PROPERTY_NAME + '"]');

    if (!input) {
      input = document.createElement('input');
      input.setAttribute('type', 'hidden');
      input.setAttribute('name', PROPERTY_NAME);
      form.appendChild(input);
    }

    input.setAttribute('value', source);
    input.value = source;
  }

  document.addEventListener('click', onClick, true);
  document.addEventListener('submit', onSubmit, true);
})();
